/**
 * Company Responsibles Tab Component
 * Displays and manages company responsibles as mini-cards
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  IconButton,
  Chip,
  Typography,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  EventBusy as EndIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import {
  CompanyResponsible,
  CompanyResponsibleFormData,
  ResponsibleType,
  RESPONSIBLE_TYPE_LABELS,
} from '../../../../types/company';
import { hojeEmSaoPaulo } from '../../../../utils/hojeEmSaoPaulo';
import { useListaPermitida } from '../../../../authorization/useListaPermitida';
import { companyService } from '../../../../services/companyService';
import { providerService } from '../../../../services/providerService';
import { ResponsibleFormModal } from '../modals';
import { ActionButton, ProfileSection } from '../../../../components/common';
import { formatarData } from '../../../flammable-installations/components/flammableDates';

// Encerrado quando a data de fim e hoje ou ja passou; so fim depois de hoje segue vigente.
const dataDeFimJaPassou = (endDate?: string | null): boolean =>
  !!endDate && endDate.slice(0, 10) <= hojeEmSaoPaulo();

interface CompanyResponsiblesTabProps {
  companyId: string;
}

const CompanyResponsiblesTab: React.FC<CompanyResponsiblesTabProps> = ({
  companyId,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  const [activeOnly, setActiveOnly] = useState(true);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedResponsible, setSelectedResponsible] = useState<CompanyResponsible | undefined>();
  const [modalError, setModalError] = useState<string | null>(null);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [responsibleToDelete, setResponsibleToDelete] = useState<CompanyResponsible | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // End dialog state
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [responsibleToEnd, setResponsibleToEnd] = useState<CompanyResponsible | null>(null);
  const [endLoading, setEndLoading] = useState(false);

  // Provider list (the professionals are loaded by the modal, by type)
  const [providers, setProviders] = useState<Array<{ id: number; name: string }>>([]);

  // Gerir responsáveis é permissão própria, e quem só lê a empresa não a tem: o cartão vinha
  // com alerta vermelho de erro para uma recusa que não é falha nenhuma.
  const carregarResponsaveis = useCallback(
    () => companyService.getResponsibles(companyId, { activeOnly }),
    [companyId, activeOnly]
  );
  const lista = useListaPermitida<CompanyResponsible>(
    'company.manage-responsibles',
    'Responsáveis',
    carregarResponsaveis
  );
  const responsibles = lista.itens;
  const loading = lista.carregando || !lista.resolvida;
  const error = lista.erro ?? null;
  const loadResponsibles = lista.recarregar;

  // Load providers (for the form modal); professionals are loaded by the modal itself, by type
  const loadSelectOptions = async () => {
    try {
      setProviders(await providerService.getProvidersForDropdown());
    } catch (err) {
      console.error('Error loading providers:', err);
    }
  };

  useEffect(() => {
    loadSelectOptions();
  }, [companyId, activeOnly]);

  // Handle add responsible
  const handleAdd = () => {
    setSelectedResponsible(undefined);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle edit responsible
  const handleEdit = (responsible: CompanyResponsible) => {
    setSelectedResponsible(responsible);
    setModalError(null);
    setModalOpen(true);
  };

  // Handle delete responsible
  const handleDeleteClick = (responsible: CompanyResponsible) => {
    setResponsibleToDelete(responsible);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!responsibleToDelete) return;

    setDeleteLoading(true);
    try {
      await companyService.deleteResponsible(companyId, responsibleToDelete.id.toString());
      enqueueSnackbar('Responsável excluído com sucesso', { variant: 'success' });
      loadResponsibles();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao excluir responsável', { variant: 'error' });
    } finally {
      setDeleteLoading(false);
      setDeleteDialogOpen(false);
      setResponsibleToDelete(null);
    }
  };

  // Handle end responsibility
  const handleEndClick = (responsible: CompanyResponsible) => {
    setResponsibleToEnd(responsible);
    setEndDialogOpen(true);
  };

  const handleEndConfirm = async () => {
    if (!responsibleToEnd) return;

    setEndLoading(true);
    try {
      await companyService.endResponsibility(companyId, responsibleToEnd.id.toString());
      enqueueSnackbar('Responsabilidade encerrada com sucesso', { variant: 'success' });
      loadResponsibles();
    } catch (err: any) {
      enqueueSnackbar(err.message ?? 'Erro ao encerrar responsabilidade', { variant: 'error' });
    } finally {
      setEndLoading(false);
      setEndDialogOpen(false);
      setResponsibleToEnd(null);
    }
  };

  // Handle form submit
  const handleFormSubmit = async (data: CompanyResponsibleFormData) => {
    setModalError(null);
    try {
      const isProviderType = [
        ResponsibleType.CLINIC_PROVIDER,
        ResponsibleType.LAB_PROVIDER,
        ResponsibleType.ENVIRONMENT_PROVIDER,
      ].includes(data.responsibleType as ResponsibleType);

      const payload = {
        responsibleType: data.responsibleType as ResponsibleType,
        providerId: isProviderType && data.providerId ? parseInt(data.providerId) : null,
        professionalId: !isProviderType && data.professionalId ? parseInt(data.professionalId) : null,
        startDate: data.startDate,
        endDate: data.endDate ?? undefined,
        notes: data.notes ?? undefined,
      };

      if (selectedResponsible) {
        await companyService.updateResponsible(
          companyId,
          selectedResponsible.id.toString(),
          payload
        );
        enqueueSnackbar('Responsável atualizado com sucesso', { variant: 'success' });
      } else {
        await companyService.createResponsible(companyId, payload);
        enqueueSnackbar('Responsável adicionado com sucesso', { variant: 'success' });
      }
      // Fim hoje ou ja passado: com "Apenas ativos" ligado o cadastro recem-feito nao apareceria.
      if (activeOnly && dataDeFimJaPassou(payload.endDate)) {
        enqueueSnackbar('Esse responsável já está encerrado; mostrando também os encerrados', {
          variant: 'info',
        });
        setActiveOnly(false);
      } else {
        loadResponsibles();
      }
    } catch (err: any) {
      setModalError(err.message ?? 'Erro ao salvar responsável');
      throw err;
    }
  };

  const isActive = (responsible: CompanyResponsible) => !dataDeFimJaPassou(responsible.endDate);

  return (
    <ProfileSection
      title="Responsaveis"
      icon={<PeopleIcon />}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Apenas ativos</Typography>}
          />
          <ActionButton
            acao="company.manage-responsibles"
            rotulo="Adicionar responsável"
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAdd}
          >
            Adicionar
          </ActionButton>
        </Box>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? null : responsibles.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography color="text.secondary" gutterBottom>
            {lista.bloqueio ?? 'Nenhum responsável cadastrado'}
          </Typography>
          {lista.permitido && (
            <Button size="small" startIcon={<AddIcon />} onClick={handleAdd}>
              Adicionar Responsável
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={2}>
          {responsibles.map((responsible) => {
            const isProviderType = [
              ResponsibleType.CLINIC_PROVIDER,
              ResponsibleType.LAB_PROVIDER,
              ResponsibleType.ENVIRONMENT_PROVIDER,
            ].includes(responsible.responsibleType);

            const name = isProviderType
              ? responsible.provider?.tradeName || responsible.provider?.legalName || '-'
              : responsible.professional?.name || '-';

            return (
              <Grid key={responsible.id} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Chip
                    label={RESPONSIBLE_TYPE_LABELS[responsible.responsibleType]}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ alignSelf: 'flex-start' }}
                  />
                  <Typography variant="subtitle2" sx={{ mt: 1 }} noWrap>
                    {name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Desde {formatarData(responsible.startDate)}
                    {responsible.endDate &&
                      ` ate ${formatarData(responsible.endDate)}`}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto',
                      pt: 1,
                    }}
                  >
                    <Chip
                      label={isActive(responsible) ? 'Ativo' : 'Encerrado'}
                      size="small"
                      color={isActive(responsible) ? 'success' : 'default'}
                    />
                    <Box>
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => handleEdit(responsible)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {isActive(responsible) && (
                        <Tooltip title="Encerrar">
                          <IconButton size="small" onClick={() => handleEndClick(responsible)}>
                            <EndIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(responsible)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Form Modal */}
      <ResponsibleFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleFormSubmit}
        responsible={selectedResponsible}
        providers={providers}
        existingResponsibles={[...responsibles]}
        error={modalError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Exclusao</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir este responsável?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* End Responsibility Confirmation Dialog */}
      <Dialog open={endDialogOpen} onClose={() => setEndDialogOpen(false)}>
        <DialogTitle>Encerrar Responsabilidade</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja encerrar esta responsabilidade? A data de fim sera
            definida como hoje.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEndDialogOpen(false)} disabled={endLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleEndConfirm}
            color="warning"
            variant="contained"
            disabled={endLoading}
          >
            {endLoading ? 'Encerrando...' : 'Encerrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </ProfileSection>
  );
};

export default CompanyResponsiblesTab;
